import { makeStyles } from '@material-ui/core';
import { Grid, Typography } from '@material-ui/core/';
import React, { useState, useEffect } from 'react';
import {
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  TextInput
} from 'react-admin';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import InvestorApi from '../../core/apis/InvestorApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

import {
  fieldLengthValidator,
  validatePhoneNumber
} from '../../core/utils/index';
import { investorStatus } from './InvestorLookups';
import { validateInvestorIdRa, isCountryWithState } from './utils';
import CheckBoxField from '../../core/components/CheckBoxField';
import { Field } from 'react-final-form';
import TagInput from '../../core/components/inputs/TagInput';

const useStyles = makeStyles({
  autocompleteSuggestions: {
    zIndex: 3010,
    backgroundColor: '#ffffff'
  },
  headerContainer: {
    paddingTop: '0px !important'
  }
});

const investorNameMaxLength = 256;
const investorIdMaxLength = 20;
const investorFaxNumberMaxLength = 32;
const investorAkaMaxLength = 255;
const investorPostalCodeMaxLength = 128;
const investorCityMaxLength = 128;
const investorAddressMaxLength = 254;
const investorTaxIdMaxLength = 50;

const InvestorDetails = props => {
  const notifyServerError = useNotifyServerError();
  const [documentHardCopyRecipient, setDocumentHardCopyRecipient] = useState(
    false
  );
  const investorId = props.record?.id;

  const [sendHardCopy, setSendHardCopy] = useState(false);
  const [addressCountry, setAddressCountry] = useState('');
  const classes = useStyles();

  const changeEnableDocumentTypeSelect = () => {
    setSendHardCopy(!sendHardCopy);
  };

  const handleCountryChange = e => {
    setAddressCountry(e.addressCountry);
    if (isCountryWithState(e.addressCountry)) {
      return true;
    } else {
      e.addressStateProvince = null;
      return false;
    }
  };

  useEffect(() => {
    TenantConfigurationApi.get('Document_Hard_Copy_Recipient')
      .then(response => {
        setDocumentHardCopyRecipient(
          response.pageData[0] && response.pageData[0].value === '1'
            ? true
            : false
        );
      })
      .catch(error => notifyServerError(error));

    if (investorId !== undefined) {
      InvestorApi.getInvestor(investorId)
        .then(response => {
          setSendHardCopy(response.investorNotificationModel.sendHardCopy);
        })
        .catch(error => notifyServerError(error));
    }
  }, [notifyServerError, investorId]);
  // show tooltip that informs the user why Investor Family menu is disabled (in case of an orphan investor)
  const showTooltip =
    (props.creation && !props.disableTooltip) ||
    (props.record && props.record.isOrphan);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.headerContainer}>
          <Typography variant="h5">Investor Details</Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="name"
            label="Investor Name"
            validate={[required(), fieldLengthValidator(investorNameMaxLength)]}
            options={{ autoComplete: 'off' }}
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="aka"
            label="A.K.A."
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorAkaMaxLength)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            disabled={!props.creation}
            source="id"
            label="Investor ID"
            validate={[
              required(),
              fieldLengthValidator(investorIdMaxLength),
              validateInvestorIdRa
            ]}
            options={{ autoComplete: 'off' }}
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="taxId"
            label="Tax ID"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorTaxIdMaxLength)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <SelectInput
            source="status"
            choices={investorStatus}
            validate={[required()]}
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <ReferenceInput
            label="Investor Family"
            source="organizationId"
            resource="organizations"
            filter={{ orphanVisibility: 'ALL_VISIBLE' }}
            perPage={-1}
            allowEmpty
            reference="organizations"
            variant="standard"
            disabled={(props.record && props.record.isOrphan) || props.creation}
          >
            <SelectInput
              optionText="name"
              helperText={
                showTooltip && "Investor doesn't have any funds assigned."
              }
            />
          </ReferenceInput>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Address and Phone</Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="addressLine1"
            label="Address Line 1"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={[
              sendHardCopy ? required() : false,
              fieldLengthValidator(investorAddressMaxLength)
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="addressLine2"
            label="Address Line 2"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorAddressMaxLength)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="addressLine3"
            label="Address Line 3"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorAddressMaxLength)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <ReferenceInput
            label="Country"
            source="addressCountry"
            resource="countries"
            reference="countries"
            filterToQuery={searchText => ({ text: searchText + '*' })}
            perPage={-1}
            variant="standard"
            validate={[sendHardCopy ? required() : false]}
          >
            <AutocompleteInput
              optionText="text"
              autoComplete="off"
              clientSideFilterOnly={true}
              options={{
                suggestionsContainerProps: {
                  className: classes.autocompleteSuggestions
                }
              }}
              id="autocomplete-input-country"
              menuId="autocomplete-input-menu-country"
              getItemId={itemIndex =>
                `autocomplete-input-menu-item-country-${itemIndex}`
              }
            />
          </ReferenceInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <FormDataConsumer>
            {({ formData, ..._rest }) =>
              formData &&
              formData.addressCountry &&
              handleCountryChange(formData) && (
                <ReferenceInput
                  key={addressCountry}
                  label="State/Province"
                  source="addressStateProvince"
                  resource="states"
                  reference="states"
                  filter={{ countryCode: formData.addressCountry }}
                  filterToQuery={searchText => ({ text: searchText + '*' })}
                  perPage={-1}
                  variant="standard"
                  parse={value => {
                    return value ? value.split(':')[0] : null;
                  }}
                  format={value => {
                    return value + ':' + formData.addressCountry;
                  }}
                >
                  <AutocompleteInput
                    clientSideFilterOnly={true}
                    optionText="text"
                    autoComplete="off"
                    options={{
                      suggestionsContainerProps: {
                        className: classes.autocompleteSuggestions
                      }
                    }}
                    id="autocomplete-input-state-province"
                    menuId="autocomplete-input-menu-state-province"
                    getItemId={itemIndex =>
                      `autocomplete-input-menu-item-state-province-${itemIndex}`
                    }
                  />
                </ReferenceInput>
              )
            }
          </FormDataConsumer>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="addressZipCode"
            label="Zip/Postal Code"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorPostalCodeMaxLength)}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="addressCity"
            label="City"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={[
              sendHardCopy ? required() : false,
              fieldLengthValidator(investorCityMaxLength)
            ]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="phoneNumber"
            label="Phone"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={validatePhoneNumber}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <TextInput
            source="faxNumber"
            label="Fax Number"
            options={{ autoComplete: 'off' }}
            variant="standard"
            validate={fieldLengthValidator(investorFaxNumberMaxLength)}
          />
        </Grid>
      </Grid>
      <FormDataConsumer>
        {() => {
          return (
            documentHardCopyRecipient && (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5">
                      Document Hard Copy Configuration
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Field
                      name="investorNotificationModel.sendHardCopy"
                      component={CheckBoxField}
                      label="Enable send copy to investor"
                      variant="standard"
                      type="checkbox"
                      onClick={changeEnableDocumentTypeSelect}
                    />
                  </Grid>
                  {sendHardCopy && (
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      <ReferenceInput
                        label="Document Type"
                        source="investorNotificationModel.documentTypeId"
                        reference="documenttypes"
                        sort={{ field: 'description', order: 'ASC' }}
                        perPage={-1}
                        variant="standard"
                        disabled={!sendHardCopy}
                      >
                        <SelectInput
                          optionText="description"
                          validate={[sendHardCopy ? required() : false]}
                        />
                      </ReferenceInput>
                    </Grid>
                  )}
                </Grid>
              </div>
            )
          );
        }}
      </FormDataConsumer>
      {!props.creation && (
        <TagInput
          id="tagInput-investor"
          label="Investor Tags"
          source="investorTags"
          resource="investortags"
          hasEditTagPermission={props.hasEditInvestorTagPermission}
        />
      )}
    </div>
  );
};
export default InvestorDetails;
